import type { LicenseHookState } from '@alchemiesolns/store-api';

export interface LicenseStatusBannerProps {
  status: LicenseHookState['status'];
  trialRemaining: number;
  trialLimit: number;
  billingConfigured: boolean;
  onSubscribe: () => void;
  onManageBilling: () => void;
}

type MetaSegment = string | { type: 'action'; label: string; action: 'subscribe' | 'billing' };

interface BannerPresentation {
  variant: 'info' | 'trial' | 'warning' | 'danger';
  badge: string;
  /** Plain sentence, or fragments with inline action link(s) embedded in copy. */
  meta: string | MetaSegment[];
  primary?: { label: string; action: 'subscribe' | 'billing' };
}

function presentation(props: LicenseStatusBannerProps): BannerPresentation {
  const { status, trialRemaining, trialLimit, billingConfigured } = props;

  switch (status) {
    case 'idle':
    case 'loading':
      /* Caller hides these — keeps switch exhaustive if types widen */
      return { variant: 'info', badge: '', meta: '', primary: undefined };
    case 'missing':
      if (trialRemaining > 0) {
        return {
          variant: 'trial',
          badge: 'Trial',
          meta: `${trialRemaining} of ${trialLimit} free trial ${trialRemaining === 1 ? 'action' : 'actions'} remaining (Generate + Export).`,
          primary: { label: 'Subscribe', action: 'subscribe' },
        };
      }
      return {
        variant: 'warning',
        badge: 'Trial',
        meta: 'You have used all free trial actions. Subscribe for full access to Piph Author.',
        primary: { label: 'Subscribe', action: 'subscribe' },
      };
    case 'past_due':
      return {
        variant: 'warning',
        badge: 'Billing',
        meta: 'Your subscription payment is past due. Update billing to avoid losing access.',
        primary: billingConfigured
          ? { label: 'Manage billing', action: 'billing' }
          : { label: 'Subscribe', action: 'subscribe' },
      };
    case 'expired':
      return {
        variant: 'danger',
        badge: 'Expired',
        meta: [
          'Your Piph Author license has expired. ',
          { type: 'action', label: 'Subscribe', action: 'subscribe' },
          ' to continue.',
        ],
      };
    case 'revoked':
      return {
        variant: 'danger',
        badge: 'Revoked',
        meta: [
          'This license is no longer valid. ',
          { type: 'action', label: 'Subscribe', action: 'subscribe' },
          ' to regain access.',
        ],
      };
    case 'error':
      return {
        variant: 'info',
        badge: 'License',
        meta: 'We could not verify your license. Check your connection, or open the Upgrade page and use Refresh license.',
        primary: { label: 'Upgrade / refresh', action: 'subscribe' },
      };
    case 'active':
      /* Caller should never render — keeps switch exhaustive */
      return { variant: 'info', badge: '', meta: '', primary: undefined };
  }
}

/**
 * Sticky banner driven by commerce license status. Caller hides when status is `active`.
 */
export function LicenseStatusBanner(props: LicenseStatusBannerProps) {
  if (props.status === 'active' || props.status === 'idle' || props.status === 'loading') {
    return null;
  }

  const view = presentation(props);

  const runAction = (action: 'subscribe' | 'billing'): void => {
    if (action === 'billing') {
      props.onManageBilling();
    } else {
      props.onSubscribe();
    }
  };

  const onPrimary = (): void => {
    if (!view.primary) return;
    runAction(view.primary.action);
  };

  const metaContent =
    typeof view.meta === 'string' ? (
      view.meta
    ) : (
      <>
        {view.meta.map((segment, index) =>
          typeof segment === 'string' ? (
            <span key={index}>{segment}</span>
          ) : (
            <button
              key={index}
              type="button"
              className="license-status-banner__inline-action"
              onClick={() => runAction(segment.action)}
            >
              {segment.label}
            </button>
          ),
        )}
      </>
    );

  return (
    <div className={`license-status-banner license-status-banner--${view.variant}`} role="status" aria-live="polite">
      {view.badge ? (
        <span className="license-status-banner__badge">{view.badge}</span>
      ) : null}
      <span className="license-status-banner__meta">{metaContent}</span>
      {view.primary ? (
        <button type="button" className="license-status-banner__action" onClick={onPrimary}>
          {view.primary.label}
        </button>
      ) : null}
    </div>
  );
}
